var _ = require("lodash");
var { TypedArrayId } = require("../../typing/typing")

/**
 * @summary
 * StatefulExpr is an object representing a value depending on a state.
 * @description
 *
  Previously StatefulExpr was a stand-alone module,
  each expression was a JSON object that was converted to JS function
  by interprete function.
 *
  Now, StatefulExpr is just a set of combinators of Lambda,
  an expression is a lambda-term, a its value for given state is calculated
  by reducing an application to the state wrapped with External.
 *
  The next step is to think about StatefulExpr as ordinary Lambda term
  evaluated in a scope with a state.
 * @module StatefulExpr
 */

/**
 * If `SElog` flag is set to `true`, an information about each reduction is printed to console.
 */
var SElog = false;

var
  C = require("./Lambda/Constructors"),
  Var = C.Var,
  Application = C.Application,
  Lambda = C.Lambda,
  External = C.External;
var
  LambdaTermId   = require("./Lambda/LambdaTermId"),
  isLambdaObject = require("./Lambda/Predicates").isLambdaObject,
  isLambdaTerm   = require("./Lambda/Predicates").isLambdaTerm,
  reduceLambdaObject = require("./Lambda/reduceLambdaObject");
var
  FnType  = require("./Types").FunctionType,
  AnyType = require("./Types").AnyType;
var
  List = require("./EInstances/Lists"),
  ListFunction = require("./EInstances/ListFunction"),
  MultivariateFunction = require("./EInstances/MultivariateFunction");


var refInterpreter = {};
var customInterpretation = {};

var lmap = {};

/**
 * StatefulExpr represents a value depending on state.
 * @see module:StatefulExpr
 * @typedef StatefulExpr
 * @global
 */

/**
 * `Pure(val)` represents a value `val` that does not depend on a state.
 * `val` is wrapped with `External` constructor of `"Object"` type.
 * @param {object} val - an object
 * @param [objType] - external object type
 * @return lambda term
 */
function Pure(val, objType) {
  var x = new C.Var();
  return new C.Lambda(x, new C.External(val, objType || "Object"));
}

/**
 * a value of term that does not depend on a state.
 * `StateIndependent(term)` is semantically equal to `Application(K, term)`
 * where `K` is the citation combinator.
 * @param {LambdaTerm} term - a lambda term
 * @return lambda term
 */
function StateIndependent(term) {
  var x = new C.Var("Object");
  return new C.Lambda(x, term);
}

/**
 * a property of a state given by a function `f`.
 * @param {(string|LambdaTerm)} f - a string of JS function or lambda term
 * @return lambda term (External Function)
 */
function Prop(f) {
  if(_.isString(f)) {
    // codomain is Object because a result is always an External object
    // therefore AnyType cannot be used
    var type = new FnType("Object", "Object");
    return new C.External(f, "Function", type);
  } else {
    return LambdaTermId(f);
  }
}

/**
 * Function of state-dependent values.
 * `Map(f, x1, x2, ...)` represents state-dependent value `t` based on other
 *   state-dependent values `x1`, `x2`, ... so that in every state state
 *   `t = f([x1, x2, ...])` holds
 * @param {(string|ListTerm)} f - string containing JS function or
 *            lambda term of ListFunction type
 * @param {...LambdaTerm} x1,x2,... - StatefulExprs
 * @return lambda term
 */
function Map(f) {
  var
    args = TypedArrayId(LambdaTermId)(_.drop([].slice.call(arguments))),
    func = _.isString(f)
         ? ListFunction(f, AnyType, "Object")
         : LambdaTermId(f),
    state = new C.Var("Object"),
    argsVal = args.map(function(a) { return C.Application(a, state); });
  if(!(func.objType === "ListFunction"))
    console.error("not a ListFunction:", func);
  return new C.Lambda(state,
    new C.Application(func,
      List.toExternal(List.List(argsVal))));
}

/**
 * Apply(f, x1, x2, ...) represents state-dependent balue t base on other
 *   state-dependent values x1, x2, ... and f, so that in every state
 *   t = f([x1, x2, ...]) holds.
 * The difference between Map and Apply is whether f is a pure function
 *   or state-dependent value returning a function.
 * @param f - state-dependent value (lambda term) that returns a function
 * @param x1, x2, ... - state-dependent values
 * @return lambda term
 */
function Apply(f) {
  var
    args = TypedArrayId(LambdaTermId)(_.drop([].slice.call(arguments))),
    func = _.isString(f)
         ? new C.External(f, "Function", new FnType("Object", "Object"))
         : LambdaTermId(f),
    state = new C.Var("Object"),
    funcVal = C.Application(func, state),
    argsVal = args.map(function(a) { return C.Application(a, state); });
  return new C.Lambda(state,
    new C.Application(funcVal,
      List.toExternal(List.List(argsVal))));  
}

function isUndefined(expr) {
  var fn = "function(arr) { return arr[0] === undefined; }";
  var f = ListFunction(fn, AnyType, "Boolean");
  return Map(f, expr);
}
/*
function ApplyLambda(term, sexpr, setype) {
  return {
    type: "ApplyLambda",
    term: LambdaTermId(term),
    sexpr: sexpr,
    setype: setype
  };
}

refInterpreter["ApplyLambda"] =
  (function(p) {
    var i = interprete(p.sexpr);
    return function(state) {
      var arg = i(state);
      var t = new C.Application(p.term, new C.External(arg, p.setype));
      return reduceLambdaObject({ type: "Lambda", term: t });
    };
  });

lmap["ApplyLambda"] = function(p, f) {
  return {
    type: "ApplyLambda",
    term: f(p.term),
    sexpr: MapLambda.call(p.sexpr, f),
    setype: p.setype
  };
};
*/

/**
 * StatefulExprValue(expr) returns (expr(state)(state)) on state.
 * @param {StatefulExpr} StatefulExpr
 * @return StatefulExpr
 */
function StatefulExprValue(expr) {
  var state = new C.Var("Object");
  return new C.Lambda(state,
    new C.Application(
      new C.Application(expr, state),
      state));
}




/**
 * interprete(expr) returns a JS function that applies to a state
 *   and returns an unwrapped result.
 * Note: if reduction to External object is impossible,
 *   lambda object is returned.
 * @param {StatefulExpr} expr - StatefulExpr
 * @return JS function of state
 */
function interprete(expr) {
  return function(state) {
    var term = new C.Application(expr, new C.External(state, "Object"));
    var r = reduceLambdaObject({ type: "Lambda", term: term });
    if(isLambdaObject(r)) {
      console.error(expr);
      console.error(state);
      console.error(r);
      throw "StatefulExpr cannot be reduced to a value";
    }
    expr.res = r;
    SElog && console.log('StatefulExpr ', _.cloneDeepWith(expr, (v) => v && v.type ? undefined : v), 'reduced to ', r);
    return r;
  };
}

/**
 * @param {StatefulExpr} expr - StatefulExpr
 * @param {Object} state - a state
 * @return a value of StatefulExpr on the given state
 */
function evaluate(expr, state) {
  return interprete(expr)(state);
}

/**
 * predicate.
 * @param {StatefulExpr} expr
 * @return {boolean}
 */
function isStatefulExpr(expr) {
  return isLambdaTerm(expr);
}

/**
 * Identification function.
 */
var StatefulExprId = LambdaTermId;



/* EXPORT */
  
var StatefulExpr = {
  StatefulExprId: StatefulExprId,
  Pure: Pure,
  StateIndependent: StateIndependent,
  Prop: Prop,
  Map:  Map,
  Apply: Apply,
  List: List,
  isUndefined: isUndefined,
  StatefulExprValue: StatefulExprValue,
  isStatefulExpr: isStatefulExpr,
  interprete: interprete,
  evaluate: evaluate,
  customInterpretation: customInterpretation,
  customLambdaMap: {},
  condition: {
    negate: function(cond) {
      var fn = "function(x) { return !x[0]; }";
      var f = ListFunction(fn, "Boolean", "Boolean");
      return Map(f, cond);
    },
    and: function() {
      var args = [].slice.call(arguments);
      // return Map("_.every", List.toExternal(List.List(args)));
      return Map.apply(this, ["_.every"].concat(args));
    },
    or: function() {
      var args = [].slice.call(arguments);
      // return Map("_.some", List.toExternal(List.List(args)));
      return Map.apply(this, ["_.some"].concat(args));
    },
    eq: function() {
      var args = [].slice.call(arguments);
      // return Map("_.isEqual", List.toExternal(List.List(args)));
      return Map.apply(this, [ListFunction("function(list) { return _.isEqual(list[0], list[1]); }", AnyType, "Boolean")].concat(args));
    }
  }
};

module.exports = StatefulExpr;
